import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, Package, FileText, Calendar, DollarSign, CreditCard, Banknote, Search, ShoppingCart, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#7FA1C3', '#F2C18D', '#90D26D', '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#10B981'];

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const YEARS = Array.from({ length: 5 }, (_, i) => {
  const year = new Date().getFullYear() - 2 + i;
  return { value: year.toString(), label: year.toString() };
});

export default function Analytics() {
  const [summary, setSummary] = useState(null);
  const [itemAnalytics, setItemAnalytics] = useState([]);
  const [vendorPerformance, setVendorPerformance] = useState([]);
  const [vendorItems, setVendorItems] = useState([]);
  const [dailySales, setDailySales] = useState([]);
  const [upiCollection, setUpiCollection] = useState(null);  // New: UPI collection data
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1 < 10 ? `0${new Date().getMonth() + 1}` : `${new Date().getMonth() + 1}`);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [viewMode, setViewMode] = useState('all'); // 'all', 'daily' or 'monthly'
  const [showUpiOnly, setShowUpiOnly] = useState(false);  // New: Filter for UPI only

  // Salesman Analytics
  const [salesmanSearch, setSalesmanSearch] = useState('');
  const [selectedSalesman, setSelectedSalesman] = useState('');
  const [salesmanData, setSalesmanData] = useState(null);
  const [allVendors, setAllVendors] = useState([]);

  // Item Demand Analytics
  const [itemSearch, setItemSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [itemDemandData, setItemDemandData] = useState(null);
  const [allItemNames, setAllItemNames] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [startDate, endDate, selectedMonth, selectedYear, viewMode]);

  useEffect(() => {
    fetchAllVendors();
    fetchAllItems();
  }, []);

  const fetchAnalytics = async () => {
    try {
      let params = {};
      
      if (viewMode === 'monthly') {
        const year = selectedYear;
        const month = selectedMonth;
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        params.start_date = `${year}-${month}-01`;
        params.end_date = `${year}-${month}-${lastDay}`;
      } else if (viewMode === 'daily' && (startDate || endDate)) {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (startDate && !endDate) {
          params.end_date = startDate;
        }
        if (!startDate && endDate) {
          const endDateObj = new Date(endDate);
          const startDateObj = new Date(endDateObj);
          startDateObj.setDate(startDateObj.getDate() - 30);
          params.start_date = startDateObj.toISOString().split('T')[0];
        }
      }
      // 'all' mode: no params = fetch everything

      console.log('Fetching analytics with params:', params);

      const [summaryRes, itemsRes, vendorsRes, vendorItemsRes, dailySalesRes, upiRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { params }),
        axios.get(`${API}/analytics/items`, { params }),
        axios.get(`${API}/analytics/vendors`, { params }),
        axios.get(`${API}/analytics/vendor-items`, { params }),
        axios.get(`${API}/analytics/daily-sales`, { params }),
        axios.get(`${API}/analytics/upi-collection`, { params }),  // Fetch UPI data
      ]);

      console.log('Analytics data received:', {
        summary: summaryRes.data,
        items: itemsRes.data.length,
        vendors: vendorsRes.data.length,
        upi: upiRes.data,
      });

      setSummary(summaryRes.data);
      setItemAnalytics(itemsRes.data);
      setVendorPerformance(vendorsRes.data);
      setVendorItems(vendorItemsRes.data);
      setDailySales(dailySalesRes.data);
      setUpiCollection(upiRes.data);  // Store UPI data
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Error loading analytics data');
    }
  };

  const fetchAllVendors = async () => {
    try {
      const res = await axios.get(`${API}/analytics/all-vendors-list`);
      setAllVendors(res.data);
    } catch (error) {
      console.error('Error fetching vendors list:', error);
    }
  };

  const fetchAllItems = async () => {
    try {
      const res = await axios.get(`${API}/analytics/all-items-list`);
      setAllItemNames(res.data);
    } catch (error) {
      console.error('Error fetching items list:', error);
    }
  };

  const fetchSalesmanData = async (name) => {
    if (!name) return;
    try {
      let params = { vendor_name: name };
      if (viewMode === 'monthly') {
        const year = selectedYear;
        const month = selectedMonth;
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        params.start_date = `${year}-${month}-01`;
        params.end_date = `${year}-${month}-${lastDay}`;
      } else if (startDate || endDate) {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const res = await axios.get(`${API}/analytics/salesman-sales`, { params });
      setSalesmanData(res.data);
    } catch (error) {
      console.error('Error fetching salesman data:', error);
      toast.error('Error loading salesman data');
    }
  };

  const fetchItemDemand = async (name) => {
    if (!name) return;
    try {
      let params = { item_name: name };
      if (viewMode === 'monthly') {
        const year = selectedYear;
        const month = selectedMonth;
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        params.start_date = `${year}-${month}-01`;
        params.end_date = `${year}-${month}-${lastDay}`;
      } else if (startDate || endDate) {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const res = await axios.get(`${API}/analytics/item-demand`, { params });
      setItemDemandData(res.data);
    } catch (error) {
      console.error('Error fetching item demand:', error);
      toast.error('Error loading item demand data');
    }
  };

  const handleSalesmanSelect = (name) => {
    setSelectedSalesman(name);
    setSalesmanSearch(name);
    fetchSalesmanData(name);
  };

  const handleItemSelect = (name) => {
    setSelectedItem(name);
    setItemSearch(name);
    fetchItemDemand(name);
  };

  const filteredVendorSuggestions = salesmanSearch
    ? allVendors.filter(v => v.toLowerCase().includes(salesmanSearch.toLowerCase()))
    : [];

  const filteredItemSuggestions = itemSearch
    ? allItemNames.filter(i => i.toLowerCase().includes(itemSearch.toLowerCase()))
    : [];

  const exportAnalyticsToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Summary
    if (summary) {
      const summaryData = [
        ['Metric', 'Value'],
        ['Total Sales', summary.total_sales],
        ['Factory Revenue (70%)', summary.factory_revenue],
        ['Total Vendors', summary.total_vendors],
        ['Total Stock Issued', summary.total_stock_issued],
        ['Total Invoices', summary.total_invoices],
        ['UPI Collection', upiCollection?.total_upi || 0],
        ['Cash Collection', upiCollection?.total_cash || 0],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 25 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');
    }

    // Sheet 2: Daily Sales
    if (dailySales.length > 0) {
      const dsData = [['Date', 'Total Sales (₹)', 'Factory Revenue (₹)']];
      dailySales.forEach(d => dsData.push([d.date, d.total_sales, d.factory_revenue]));
      const ws2 = XLSX.utils.aoa_to_sheet(dsData);
      ws2['!cols'] = [{ wch: 14 }, { wch: 18 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Daily Sales');
    }

    // Sheet 3: Item Analytics
    if (itemAnalytics.length > 0) {
      const iaData = [['Item Name', 'Total Quantity', 'Total Value (₹)']];
      itemAnalytics.forEach(i => iaData.push([i.item_name, i.total_quantity, i.total_value]));
      const ws3 = XLSX.utils.aoa_to_sheet(iaData);
      ws3['!cols'] = [{ wch: 25 }, { wch: 16 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Item Analytics');
    }

    // Sheet 4: Vendor Performance
    if (vendorPerformance.length > 0) {
      const vpData = [['Vendor Name', 'Total Sales (₹)', 'Invoice Count']];
      vendorPerformance.forEach(v => vpData.push([v.vendor_name, v.total_sales, v.invoice_count]));
      const ws4 = XLSX.utils.aoa_to_sheet(vpData);
      ws4['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws4, 'Vendor Performance');
    }

    // Sheet 5: UPI Details
    if (upiCollection?.vendor_upi_details?.length > 0) {
      const upiData = [['Salesman', 'UPI Amount (₹)', 'Date']];
      upiCollection.vendor_upi_details.forEach(u => upiData.push([u.vendor_name, u.upi_amount, u.date]));
      const ws5 = XLSX.utils.aoa_to_sheet(upiData);
      ws5['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws5, 'UPI Collection');
    }

    const dateStr = viewMode === 'monthly' ? `${selectedYear}-${selectedMonth}` : viewMode === 'all' ? 'All-Time' : `${startDate || 'start'}_${endDate || 'end'}`;
    XLSX.writeFile(wb, `Convella_Analytics_${dateStr}.xlsx`);
    toast.success('Analytics exported to Excel');
  };

  const statCards = [
    {
      title: viewMode === 'monthly' ? 'Total Sales (Month)' : viewMode === 'all' ? 'Total Sales (All Time)' : 'Total Sales',
      value: summary ? `₹${summary.total_sales.toFixed(2)}` : '₹0',
      icon: TrendingUp,
      color: 'bg-[#7FA1C3]',
      testId: 'total-sales-card',
    },
    {
      title: viewMode === 'monthly' ? 'Factory Revenue (Month)' : 'Factory Revenue (70%)',
      value: summary ? `₹${summary.factory_revenue.toFixed(2)}` : '₹0',
      icon: DollarSign,
      color: 'bg-[#90D26D]',
      testId: 'factory-revenue-card',
    },
    {
      title: 'UPI Collection',
      value: upiCollection ? `₹${upiCollection.total_upi.toFixed(2)}` : '₹0',
      icon: CreditCard,
      color: 'bg-purple-500',
      testId: 'upi-collection-card',
    },
    {
      title: 'Cash Collection',
      value: upiCollection ? `₹${upiCollection.total_cash.toFixed(2)}` : '₹0',
      icon: Banknote,
      color: 'bg-green-600',
      testId: 'cash-collection-card',
    },
    {
      title: viewMode === 'monthly' ? 'Total Vendors (Month)' : 'Total Vendors',
      value: summary ? summary.total_vendors : 0,
      icon: Users,
      color: 'bg-[#F2C18D]',
      testId: 'total-vendors-card',
    },
    {
      title: viewMode === 'monthly' ? 'Total Stock Issued (Month)' : 'Total Stock Issued',
      value: summary ? `₹${summary.total_stock_issued.toFixed(2)}` : '₹0',
      icon: Package,
      color: 'bg-[#8B5CF6]',
      testId: 'total-stock-card',
    },
    {
      title: viewMode === 'monthly' ? 'Total Invoices (Month)' : 'Total Invoices',
      value: summary ? summary.total_invoices : 0,
      icon: FileText,
      color: 'bg-[#EC4899]',
      testId: 'total-invoices-card',
    },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-[#7FA1C3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Analytics Dashboard
          </h1>
          <p className="text-slate-600 mt-2">Track your business performance</p>
        </div>

        <div className="flex gap-3 flex-wrap items-start">
          <Button
            onClick={exportAnalyticsToExcel}
            data-testid="export-analytics-excel"
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-semibold"
          >
            <Download size={16} className="mr-2" />
            Export to Excel
          </Button>
          <Card className="shadow-sm border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex gap-2">
                  <Button
                    onClick={() => setViewMode('all')}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      viewMode === 'all'
                        ? 'bg-[#7FA1C3] text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="view-mode-all"
                  >
                    All Time
                  </Button>
                  <Button
                    onClick={() => setViewMode('daily')}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      viewMode === 'daily'
                        ? 'bg-[#7FA1C3] text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="view-mode-daily"
                  >
                    Date Range
                  </Button>
                  <Button
                    onClick={() => setViewMode('monthly')}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      viewMode === 'monthly'
                        ? 'bg-[#7FA1C3] text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="view-mode-monthly"
                  >
                    Monthly
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {viewMode === 'daily' && (
            <Card className="shadow-sm border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Calendar size={20} className="text-[#7FA1C3]" />
                  <div className="flex items-center gap-2">
                    <div>
                      <Label htmlFor="start-date" className="text-xs mb-1 block">
                        From
                      </Label>
                      <Input
                        id="start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                        }}
                        data-testid="filter-start-date"
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end-date" className="text-xs mb-1 block">
                        To
                      </Label>
                      <Input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                        }}
                        data-testid="filter-end-date"
                        className="text-sm"
                      />
                    </div>
                    {(startDate || endDate) && (
                      <Button
                        onClick={() => {
                          setStartDate('');
                          setEndDate('');
                          toast.info('Filters cleared');
                        }}
                        className="mt-5 bg-slate-200 hover:bg-slate-300 text-slate-700 px-3 py-2 rounded-full text-xs"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {viewMode === 'monthly' && (
            <Card className="shadow-sm border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Calendar size={20} className="text-[#7FA1C3]" />
                  <div className="flex items-center gap-2">
                    <div>
                      <Label htmlFor="month" className="text-xs mb-1 block">
                        Month
                      </Label>
                      <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                        <SelectTrigger id="month" className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((month) => (
                            <SelectItem key={month.value} value={month.value}>
                              {month.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="year" className="text-xs mb-1 block">
                        Year
                      </Label>
                      <Select value={selectedYear} onValueChange={setSelectedYear}>
                        <SelectTrigger id="year" className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {YEARS.map((year) => (
                            <SelectItem key={year.value} value={year.value}>
                              {year.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.title} className="shadow-sm border-slate-200 card-hover" data-testid={card.testId}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 font-medium">{card.title}</p>
                    <p className="text-3xl font-extrabold mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {card.value}
                    </p>
                  </div>
                  <div className={`${card.color} p-3 rounded-xl text-white`}>
                    <Icon size={28} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {dailySales.length > 0 && (
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {viewMode === 'monthly' ? 'Daily Sales & Factory Revenue (Month)' : 'Daily Sales & Factory Revenue'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={dailySales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                />
                <Legend />
                <Line type="monotone" dataKey="total_sales" stroke="#7FA1C3" strokeWidth={2} name="Total Sales (₹)" dot={{ fill: '#7FA1C3' }} />
                <Line type="monotone" dataKey="factory_revenue" stroke="#90D26D" strokeWidth={2} name="Factory Revenue (₹)" dot={{ fill: '#90D26D' }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {upiCollection && upiCollection.daily_data && upiCollection.daily_data.length > 0 && (
        <Card className="shadow-sm border-slate-200" data-testid="upi-collection-chart">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-green-50 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                UPI vs Cash Collection
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowUpiOnly(false)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                    !showUpiOnly
                      ? 'bg-slate-700 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="upi-filter-all"
                >
                  All
                </Button>
                <Button
                  onClick={() => setShowUpiOnly(true)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                    showUpiOnly
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="upi-filter-upi-only"
                >
                  UPI Only
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={upiCollection.daily_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                  formatter={(value) => [`₹${value.toFixed(2)}`]}
                />
                <Legend />
                <Bar dataKey="upi_amount" fill="#8B5CF6" name="UPI (₹)" radius={[4, 4, 0, 0]} />
                {!showUpiOnly && (
                  <Bar dataKey="cash_amount" fill="#10B981" name="Cash (₹)" radius={[4, 4, 0, 0]} />
                )}
              </BarChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-8 mt-4 text-sm">
              <div className="flex items-center gap-2" data-testid="upi-total-display">
                <CreditCard size={16} className="text-purple-500" />
                <span className="font-semibold">Total UPI: ₹{upiCollection.total_upi.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2" data-testid="cash-total-display">
                <Banknote size={16} className="text-green-600" />
                <span className="font-semibold">Total Cash: ₹{upiCollection.total_cash.toFixed(2)}</span>
              </div>
            </div>
            {/* Vendor-wise UPI Details Table */}
            {upiCollection.vendor_upi_details && upiCollection.vendor_upi_details.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <h3 className="font-semibold text-sm mb-3 text-purple-700">UPI Payments by Salesman</h3>
                <div className="overflow-x-auto max-h-60 overflow-y-auto">
                  <table className="w-full text-sm" data-testid="upi-vendor-details-table">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-semibold">Salesman</th>
                        <th className="text-right py-2 px-3 font-semibold">UPI Amount</th>
                        <th className="text-left py-2 px-3 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upiCollection.vendor_upi_details.map((entry, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-purple-50/50">
                          <td className="py-2 px-3 font-medium">{entry.vendor_name}</td>
                          <td className="py-2 px-3 text-right font-semibold text-purple-600">₹{entry.upi_amount.toFixed(2)}</td>
                          <td className="py-2 px-3 text-slate-600">{entry.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Item-wise Sales
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {itemAnalytics.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={itemAnalytics}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="item_name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={100} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Bar dataKey="total_quantity" fill="#7FA1C3" name="Quantity" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <p>No data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Product Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {itemAnalytics.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={itemAnalytics.slice(0, 8)}
                    dataKey="total_quantity"
                    nameKey="item_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => entry.item_name}
                  >
                    {itemAnalytics.slice(0, 8).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <p>No data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {viewMode === 'monthly' && itemAnalytics.length > 0 && (
        <Card className="shadow-sm border-slate-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Monthly Item Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-sm">Item Name</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Total Quantity</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  {itemAnalytics.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium">{item.item_name}</td>
                      <td className="py-3 px-4 text-sm text-right font-semibold text-[#7FA1C3]">
                        {item.total_quantity}
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-semibold text-[#90D26D]">
                        ₹{item.total_value.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-bold">
                    <td className="py-3 px-4 text-sm">TOTAL</td>
                    <td className="py-3 px-4 text-sm text-right text-[#7FA1C3]">
                      {itemAnalytics.reduce((sum, item) => sum + item.total_quantity, 0)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-[#90D26D]">
                      ₹{itemAnalytics.reduce((sum, item) => sum + item.total_value, 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== SALESMAN ANALYTICS ==================== */}
      <Card className="shadow-sm border-slate-200" data-testid="salesman-analytics-card">
        <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b">
          <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Users size={22} className="text-teal-600" />
            Salesman Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-4">
            <Label className="text-sm font-semibold mb-2 block">Search Salesman</Label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={salesmanSearch}
                onChange={(e) => {
                  setSalesmanSearch(e.target.value);
                  if (!e.target.value) { setSelectedSalesman(''); setSalesmanData(null); }
                }}
                placeholder="Type salesman/vendor name..."
                data-testid="salesman-search-input"
                className="pl-9"
              />
              {salesmanSearch && !selectedSalesman && filteredVendorSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredVendorSuggestions.map((name) => (
                    <button
                      key={name}
                      onClick={() => handleSalesmanSelect(name)}
                      className="w-full text-left px-4 py-2 hover:bg-teal-50 text-sm transition-colors"
                      data-testid={`salesman-option-${name}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {salesmanData && salesmanData.daily_data.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-teal-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Salesman</p>
                  <p className="text-xl font-bold text-teal-700">{salesmanData.vendor_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Total Sales</p>
                  <p className="text-xl font-bold text-teal-700">₹{salesmanData.total_sales.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Active Days</p>
                  <p className="text-xl font-bold text-teal-700">{salesmanData.total_days}</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesmanData.daily_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                    formatter={(value) => [`₹${value.toFixed(2)}`]}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="total_sales" stroke="#0D9488" strokeWidth={2} name="Total Sales (₹)" dot={{ fill: '#0D9488' }} />
                  <Line type="monotone" dataKey="factory_share" stroke="#90D26D" strokeWidth={2} name="Factory Share (₹)" dot={{ fill: '#90D26D' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : selectedSalesman ? (
            <div className="text-center py-8 text-slate-400">
              <p>No sales data found for "{selectedSalesman}"</p>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <p>Search and select a salesman to view their performance</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ==================== ITEM DEMAND ANALYTICS ==================== */}
      <Card className="shadow-sm border-slate-200" data-testid="item-demand-analytics-card">
        <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 border-b">
          <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ShoppingCart size={22} className="text-amber-600" />
            Item Demand Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-4">
            <Label className="text-sm font-semibold mb-2 block">Search Item</Label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={itemSearch}
                onChange={(e) => {
                  setItemSearch(e.target.value);
                  if (!e.target.value) { setSelectedItem(''); setItemDemandData(null); }
                }}
                placeholder="Type item name (e.g., Orange, Chocobar)..."
                data-testid="item-demand-search-input"
                className="pl-9"
              />
              {itemSearch && !selectedItem && filteredItemSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredItemSuggestions.map((name) => (
                    <button
                      key={name}
                      onClick={() => handleItemSelect(name)}
                      className="w-full text-left px-4 py-2 hover:bg-amber-50 text-sm transition-colors"
                      data-testid={`item-option-${name}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {itemDemandData && itemDemandData.daily_data.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-amber-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Item</p>
                  <p className="text-xl font-bold text-amber-700">{itemDemandData.item_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Total Sold</p>
                  <p className="text-xl font-bold text-amber-700">{itemDemandData.total_quantity} units</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Total Revenue</p>
                  <p className="text-xl font-bold text-amber-700">₹{itemDemandData.total_revenue.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600">Active Days</p>
                  <p className="text-xl font-bold text-amber-700">{itemDemandData.total_days}</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={itemDemandData.daily_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Bar dataKey="quantity" fill="#F59E0B" name="Quantity Sold" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : selectedItem ? (
            <div className="text-center py-8 text-slate-400">
              <p>No demand data found for "{selectedItem}"</p>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <p>Search and select an item to view demand trends</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Vendor Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {vendorPerformance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="vendor-performance-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-sm">Vendor Name</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Total Sales</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Invoice Count</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorPerformance.map((vendor, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 text-sm">{vendor.vendor_name}</td>
                      <td className="py-3 px-4 text-sm text-right font-semibold text-[#7FA1C3]">
                        ₹{vendor.total_sales.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-sm text-right">{vendor.invoice_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <p>No data available</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Vendor-wise Daily Sales & Items
            </CardTitle>
            <div className="relative w-64">
              <Input
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                placeholder="Search vendor..."
                data-testid="vendor-search-input"
                className="pl-3"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {vendorItems.length > 0 ? (
            <div className="space-y-6">
              {vendorItems
                .filter(vendorData => 
                  vendorData.vendor_name.toLowerCase().includes(vendorSearch.toLowerCase())
                )
                .map((vendorData, idx) => {
                  const totalQuantity = vendorData.items.reduce((sum, item) => sum + item.quantity, 0);
                  const totalSales = vendorData.items.reduce((sum, item) => sum + item.total, 0);
                  
                  return (
                    <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                      <div className="bg-gradient-to-r from-[#7FA1C3]/10 to-[#F2C18D]/10 p-4 border-b">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-bold text-xl text-[#7FA1C3]">{vendorData.vendor_name}</h3>
                            <span className="text-sm text-slate-600">Date: {vendorData.date}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-slate-600">Total Quantity</div>
                            <div className="text-2xl font-bold text-[#7FA1C3]">{totalQuantity}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-slate-600">Total Sales</div>
                            <div className="text-2xl font-bold text-[#90D26D]">₹{totalSales.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {vendorData.items.map((item, itemIdx) => (
                            <div
                              key={itemIdx}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-[#7FA1C3] transition-colors"
                            >
                              <div className="flex-1">
                                <p className="font-semibold text-sm">{item.item_name}</p>
                                <p className="text-xs text-slate-500">₹{item.price} × {item.quantity} qty</p>
                              </div>
                              <div className="font-bold text-[#7FA1C3]">₹{item.total}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              {vendorItems.filter(vendorData => 
                vendorData.vendor_name.toLowerCase().includes(vendorSearch.toLowerCase())
              ).length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <p>No vendors found matching "{vendorSearch}"</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <p>No vendor item data available</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}